import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Grid, CircularProgress, Alert, Divider, Paper,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';

const TERMS = [
  'The accredited CAB shall use the NABL Accredited CAB Combined ILAC MRA Mark only after this agreement has been signed by NABL accredited CAB and NABL.',
  'Accredited CABs can use the NABL Accredited CAB Combined ILAC MRA Mark for activities undertaken outside the economy in which they are established.',
  'The accredited CAB shall ensure that, at all times, its use of the NABL Accredited CAB Combined ILAC MRA Mark does not breach any requirements of NABL 133.',
  'NABL Accredited CAB Combined ILAC MRA Mark shall only be reproduced in the manner prescribed in Section 9 of NABL 133.',
  'The ILAC MRA Mark shall be positioned at the left side of the NABL symbol, but shall appear in close proximity to each other.',
  'NABL accredited CAB shall not use the NABL Accredited CAB Combined ILAC MRA Mark in any way that would harm the reputation of ILAC and/or NABL.',
  'NABL accredited CAB shall allow NABL to observe the use of the Accredited CAB Combined ILAC MRA Mark.',
  'NABL accredited CABs are entitled to use of the NABL Accredited CAB Combined ILAC MRA Mark together with the certificate number of the accredited CAB.',
  'NABL accredited CAB shall not use the ILAC MRA Mark in any form other than as part of a NABL Accredited CAB Combined ILAC MRA Mark.',
  'Upon the termination of this agreement, the accredited CAB no longer has the right to use the NABL Accredited CAB Combined ILAC MRA Mark and stop all such use.',
  "The accredited CAB's rights under this agreement shall automatically and immediately be suspended during any period where the accredited CAB either voluntary withdraws its Accreditation or the accreditation status of the CAB is under \"Voluntary Withdrawal\" or \"Suspension\" or \"Forced Withdrawal\" or \"Debarred\" category.",
  'NABL has the right to terminate this agreement by giving a written notice if the accredited CAB is found violating the terms and conditions of this agreement.',
  'This agreement will automatically terminate when CAB Acquisition, Merger, De-Merger, Take over/ Purchase and Selling of CAB has been initiated as informed to NABL.',
  'This agreement will automatically terminate when ILAC MRA status of NABL has been suspended, terminated or not renewed by ILAC.',
  'In case, there is any on-going complaint against the accredited CAB, NABL shall not permit the use of NABL Accredited CAB Combined ILAC MRA Mark to the particular CAB.',
  'All disputes, if any, arising out of this contract, that remain unresolved through mechanism provided by NABL are subject to the exclusive jurisdiction of the Courts at New Delhi and none other.',
];

const TERM_LABELS = 'ABCDEFGHIJKLMNOP'.split('');

const decodeData = (encoded) => {
  try {
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const paddingNeeded = (4 - base64.length % 4) % 4;
    const padded = base64 + '='.repeat(paddingNeeded);
    return JSON.parse(decodeURIComponent(escape(atob(padded))));
  } catch {
    return null;
  }
};

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', mb: 1 }}>
    <Typography sx={{ minWidth: 200, fontWeight: 500, textAlign: 'right', pr: 2 }}>
      {label}:
    </Typography>
    <Typography>{value || ''}</Typography>
  </Box>
);

const IlacReport = () => {
  const location = useLocation();
  const [certData, setCertData] = useState(null);
  const [ilacData, setIlacData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useState(null);
  const [importantNoteOpen, setImportantNoteOpen] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const encoded = searchParams.get('data');
    if (!encoded) {
      setError('Missing report data in URL.');
      setLoading(false);
      return;
    }
    const decoded = decodeData(encoded);
    if (!decoded || !decoded.appId || !decoded.orgId) {
      setError('Invalid report data in URL.');
      setLoading(false);
      return;
    }
    setParams(decoded);
  }, [location.search]);

  useEffect(() => {
    if (!params) return;
    const { appId, orgId } = params;
    const base = process.env.REACT_APP_API_URL;

    setLoading(true);
    Promise.all([
      axios.get(`${base}/ilac/certificate/${appId}`),
      axios.get(`${base}/ilac/data/${appId}/${orgId}`),
    ])
      .then(([certRes, ilacRes]) => {
        if (certRes.data.success) setCertData(certRes.data.data);
        if (ilacRes.data.success) setIlacData(ilacRes.data.data);
      })
      .catch(() => setError('Failed to load ILAC report data.'))
      .finally(() => setLoading(false));
  }, [params]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const labNameForDeclaration = ilacData
    ? `${ilacData.CabName || ''} (${certData.StateName || ''}) (${certData.CityName || ''}) (${ilacData.ApplicationNo || ''})`
    : params?.labName || '';

  const labType = ilacData?.ApplicationType || params?.labType || '';

  return (
    <Box sx={{ p: 3, mt:5, maxWidth: 960, mx: 'auto' }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<InfoIcon />}
            onClick={() => setImportantNoteOpen(true)}
            sx={{ borderColor: '#f57c00', color: '#f57c00', '&:hover': { borderColor: '#e65100', bgcolor: '#fff3e0' } }}
          >
            Important Notice for Laboratory
          </Button>
          <Typography variant="h5" fontWeight="bold">Appendix A of NABL 133</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">
            Agreement for use of NABL Accredited CAB Combined ILAC MRA Mark
          </Typography>
        </Box>

        {/* CAB Info */}
        <Box sx={{ mb: 3 }}>
          <InfoRow label="CAB Name" value={certData?.CabName} />
          <InfoRow label="CAB ID" value={certData?.ApplicationNo} />
          <InfoRow label="Certificate Number" value={certData?.Cert_No} />
          <InfoRow label="Valid upto" value={certData?.ToDate} />
          <InfoRow label="Address" value={certData?.PrimeAddress} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Terms and Conditions */}
        <Typography variant="h5" align="center" fontWeight="bold" sx={{ mb: 3 }}>
          Terms and Conditions for Use of NABL Accredited CAB Combined ILAC MRA Mark
        </Typography>

        <Box component="ol" sx={{ listStyle: 'none', pl: 0, mb: 3 }}>
          {TERMS.map((term, idx) => (
            <Box component="li" key={idx} sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
              <Typography fontWeight="bold" sx={{ minWidth: 28 }}>
                {TERM_LABELS[idx]}.
              </Typography>
              <Typography sx={{ textAlign: 'justify' }}>{term}</Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Declaration */}
        <Box sx={{ mb: 3 }}>
          <Typography fontWeight="bold" gutterBottom>Declaration</Typography>
          <Typography sx={{ textAlign: 'justify' }}>
            I declare that, undersigned is authorized to sign this agreement on behalf of{' '}
            <strong>{labNameForDeclaration}</strong> which is accredited by NABL in the field of{' '}
            <strong>{labType}</strong>.
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Acceptance Details */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography fontWeight="bold" gutterBottom>Authorised Representative of CAB</Typography>
            <Box sx={{ mt: 1 }}>
              <Typography gutterBottom>
                Name of Head of the Laboratory: <strong>{ilacData?.Name || ''}</strong>
              </Typography>
              <Typography gutterBottom>
                Designation: <strong>{ilacData?.Designation || ''}</strong>
              </Typography>
              <Typography gutterBottom>
                Submitted Date: <strong>{ilacData?.SubmitDate || ''}</strong>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography fontWeight="bold" gutterBottom>NABL Official</Typography>
            <Box sx={{ mt: 1 }}>
              <Typography gutterBottom>
                Name of NABL Official: <strong>{ilacData?.DealingOfficer || ''}</strong>
              </Typography>
              <Typography gutterBottom>
                Designation: <strong>Joint Director</strong>
              </Typography>
              <Typography gutterBottom>
                Permission No.: <strong>{ilacData?.IlackNo || ''}</strong>
              </Typography>
              <Typography gutterBottom>
                Date of Permission: <strong>{ilacData?.IlackNo_Date || ''}</strong>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Important Note Dialog */}
      <Dialog open={importantNoteOpen} onClose={() => setImportantNoteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon sx={{ color: '#f57c00' }} />
          Important Notice
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography paragraph sx={{ textAlign: 'justify' }}>
            CAB may note that at any point of time during the application process or assessment process, if there is an evidence of fraudulent behaviour or if any false information is provided or if any information is concealed, NABL will reject the application and it's processing or if in accreditation process, then terminate the assessment process.
          </Typography>
          <Typography sx={{ textAlign: 'justify' }}>
            NABL shall refuse to service an application because of proven fraudulent behaviour or falsification of information or violation of accreditation requirements.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={() => setImportantNoteOpen(false)}
            sx={{ bgcolor: '#f57c00', '&:hover': { bgcolor: '#e65100' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IlacReport;